import {
  Wrap,
  Flex,
  HStack,
  Text,
  Grid,
} from '@chakra-ui/react';
import InformationSourcePopup from '@src/modules/performance/components/InformationSourcePopup';
import LoadingSpinner from '@src/components/shared/LoadingSpinner';
import { Fragment, useEffect } from 'react';
import { useRecoilState } from 'recoil';
import { selectedCustomReportState } from '@src/modules/performance/state';
import { useLocation } from 'react-router-dom';
import { availableReportComponents } from '../shared/availableReportComponents';
import { useCustomReportsList, useCustomReport } from '../context/custom-reports-api-hooks';
import CustomReportsSelector from './CustomReportsSelector';

function ReportTitle({ subTitle, data, children }) {
  return (
    <HStack width="100%" mb={4} justifyContent="space-between" align="center" className="hidden-print">
      <HStack>
        <Text display="block" as="span" fontSize="2xl" color="gray.500">
          {subTitle}
        </Text>
        {data?.description && (
          <InformationSourcePopup
            title={data.name}
            content={[{
              title: null,
              description: <Text fontSize="inherit">{data.description}</Text>,
              source: null,
            }]}
          />
        )}
      </HStack>
      <HStack>
        <CustomReportsSelector />
        {children && children}
      </HStack>
    </HStack>
  );
}

function CustomReportsView({ children }) {
  const { pathname } = useLocation();
  const page = pathname.split('/')[1]?.toLowerCase();
  const [selectedCustomReports, setSelectedCustomReports] = useRecoilState(selectedCustomReportState);
  const { data: customReports = [], isLoading: isReportsLoading } = useCustomReportsList();

  const filteredReports = customReports.filter(
    (report) => report.page?.map((p) => p.toLowerCase()).includes(page),
  );

  const selectedReportId = selectedCustomReports?.[page];
  const selectedReportExists = filteredReports.some((r) => r.id === selectedReportId);
  const fallbackReport = filteredReports[0];

  // Sync fallback to state if needed
  useEffect(() => {
    if (!selectedReportId || !selectedReportExists) {
      if (fallbackReport) {
        setSelectedCustomReports((prev) => ({
          ...prev,
          [page]: fallbackReport.id,
        }));
      }
    }
  }, [selectedReportId, selectedReportExists, fallbackReport, page, setSelectedCustomReports]);

  const { data, isLoading: isReportLoading } = useCustomReport(selectedReportId);

  const componentsList = data?.components;
  // Determine if the report is a dashboard with widgets only
  // if all components are type of 'dashboard-widget'
  const isDashboardWidgetsOnly = componentsList?.every((id) => {
    const component = availableReportComponents.find(({ id: compId }) => compId === id);
    return component && component.type === 'dashboard-widget';
  });
  const subTitle = data ? data.name : 'Performance Dashboard';

  if (isReportsLoading || isReportLoading || !selectedReportId || !selectedReportExists) {
    return (
      <Flex
        justifyContent="center"
        alignItems="center"
        height="100%"
        minH="300px"
        flexGrow={1}
      >
        <LoadingSpinner />
      </Flex>
    );
  }

  if (isDashboardWidgetsOnly) {
    return (
      <>
        <ReportTitle subTitle={subTitle} data={data}>
          {children && children}
        </ReportTitle>
        <Grid
          className="dashboard-grid"
          gridTemplateColumns="repeat(auto-fit, minmax(250px, 1fr))"
          gap={2}
          sx={{
            '@media print': {
              gridTemplateColumns: 'repeat(6, minmax(64mm,1fr))!important',
              gap: '2mm',
            },
          }}
        >
          {componentsList?.map((id) => {
            const component = availableReportComponents.find(({ id: compId }) => compId === id);
            return component ? <Fragment key={id}>{component.component}</Fragment> : null;
          })}
        </Grid>
      </>
    );
  }

  return (
    <>
      <ReportTitle subTitle={subTitle} data={data}>
        {children && children}
      </ReportTitle>
      <Wrap spacing={2} className="report-sections-wrapper">
        {componentsList?.map((id) => {
          const component = availableReportComponents.find(({ id: compId }) => compId === id);
          return component ? (
            <Flex key={id} flexGrow="1">
              {component.component}
            </Flex>
          ) : null;
        })}
      </Wrap>
    </>
  );
}

export default CustomReportsView;
